import * as React from 'react';
import { classNames, useUid } from '@/utils';

export interface TextareaProps {
  /** The ID of the textarea element. */
  id?: string;
  /** The number of rows to display in the textarea. */
  rows?: number;
  /** An error message to display below the textarea, if applicable. */
  error?: string;
  /** Whether the textarea should be disabled. */
  disabled?: boolean;
  /** Placeholder text to display in the textarea. */
  placeholder?: string;
  /** The default value of the textarea. */
  defaultValue?: string;
  /** The label to display above the textarea. */
  label?: React.ReactNode;
  /** CSS class names to apply to the label. */
  labelClassName?: string;
  /** CSS class names to apply to the textarea. */
  inputClassName?: string;
  /** Help text to display below the textarea. */
  helpText?: React.ReactNode;
  /** Additional styles to apply to the textarea. */
  style?: React.CSSProperties;
  /** A callback function that is called when the value of the textarea changes. */
  onChange?(value: string): void;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
  const {
    label,
    error,
    id,
    style,
    helpText,
    disabled,
    labelClassName,
    inputClassName,
    rows,
    onChange,
    defaultValue,
    placeholder,
  } = props;

  const uid = useUid(id);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event.currentTarget.value);
  };

  return (
    <label htmlFor={uid} className={classNames({ error }, labelClassName)}>
      <p>{label}</p>
      <textarea
        id={uid}
        ref={ref}
        rows={rows}
        style={style}
        disabled={disabled}
        onChange={handleChange}
        placeholder={placeholder}
        defaultValue={defaultValue}
        className={classNames(inputClassName)}
      />
      <small>{error || helpText}</small>
    </label>
  );
});

Textarea.displayName = 'Textarea';
